import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, Observable, Subject, tap} from 'rxjs';
import { enviroment } from 'src/environments/environment';
import { uploadPartsConcurrently as uploadMultipartParts } from 'src/app/shared/upload/multipart-parts';

export const globalUploadProgress$ = new BehaviorSubject<number>(0);

@Injectable({ providedIn: "root" })
export class FileService {
  fileUrl: string;
  fileUrlBulkUploadDownload: string;
  httpOptions: {};
  idToken: string;

  constructor(private http: HttpClient) {
    this.fileUrl = enviroment.BASE_URL_FILES + "/files/user";

    this.fileUrlBulkUploadDownload = enviroment.BASE_URL_FILES + "/file/user";
    this.idToken = sessionStorage.getItem("idToken") || "";
    this.httpOptions = {
      // headers: new HttpHeaders({
      //   'Content-Type': 'application/json; charset=utf-8',
      //   'Authorization': `Bearer ${this.idToken}`
      // })
    };
  }

  HeadFile(
    userID: string,
    projectID: string,
    folder: string,
    fileName: string
  ): Observable<HttpResponse<any>> {
    const url = `${this.fileUrl}/${userID}/project/${projectID}/${folder}/${fileName}`;
    return this.http.head<any>(url, {
      ...this.httpOptions,
      observe: "response",
    });
  }

  PostFile(
    userID: string,
    projectID: string,
    folder: string,
    fileName: string,
    uploadId: string,
    partNumber: number,
    file: File
  ): Observable<HttpResponse<any>> {
    const url = `${this.fileUrl}/${userID}/project/${projectID}/${folder}/${fileName}?upload_id=${uploadId}&part_number=${partNumber}`;
    const formData = new FormData();
    formData.append("file", file, file.name);
    return this.http.post<any>(url, formData, {
      ...this.httpOptions,
      observe: "response",
    });
  }

  PutFile(
    userID: string,
    projectID: string,
    folder: string,
    fileName: string,
    uploadId: string,
    parts: { ETag: string; PartNumber: number }[]
  ): Observable<HttpResponse<any>> {
    const url = `${this.fileUrl}/${userID}/project/${projectID}/${folder}/${fileName}?upload_id=${uploadId}`;
    return this.http.put<any>(url, parts, {
      ...this.httpOptions,
      observe: "response",
    });
  }

  downloadFile(
    userID: string,
    projectID: string,
    folder: string,
    fileName: string,
    start: number = 0,
    end: number = 10483712,
    fileID: string
  ): Observable<HttpResponse<Blob>> {
    const url = `${this.fileUrl}/${userID}/project/${projectID}/${folder}/${fileID}`;
    const httpOptions = {
      headers: new HttpHeaders({
        Range: `bytes=${start}-${end}`,
        Authorization: `Bearer ${this.idToken}`,
      }),
      observe: "response" as const,
      responseType: "blob" as "json",
    };
    return this.http.get<Blob>(url, httpOptions);
  }

  /**
   * Persiste arquivo no banco (etapa 4 do upload) usando o endpoint unificado.
   * POST /user/{userID}/upload/{type}
   */
  persistFileUnified(
    userID: string,
    type: 'map' | 'log',
    body: { file: string; projectID?: string | null; vcuID?: string | null; pathFile?: string | null }
  ): Observable<HttpResponse<any>> {
    const url = `${enviroment.BASE_URL_API}/user/${userID}/upload/${type}`;
    return this.http.post<any>(url, body, { ...this.httpOptions, observe: 'response' });
  }

  /**
   * Inicia upload no nível do usuário (sem projectID/taskID).
   * POST /files/user/{userID}/upload/start — backend devolve `objectName` no body e `Upload-Id` no header.
   */
  StartUserFileUpload(
    userID: string,
    type: 'map' | 'log',
    file: File
  ): Observable<HttpResponse<any>> {
    const url = `${this.fileUrl}/${userID}/upload/start`;
    const body = {
      file: file.name,
      contentType: 'application/octet-stream',
      folder: type,
      size: file.size / 1024,
    };
    return this.http.post<any>(url, body, { ...this.httpOptions, observe: 'response' });
  }

  /**
   * Partes em paralelo com retry/backoff — algoritmo compartilhado em
   * `shared/upload/multipart-parts.ts` (também usado pela mídia social). Aqui só se acrescenta
   * a emissão do progresso em `globalUploadProgress$`.
   */
  private uploadPartsConcurrently(
    file: File,
    partSize: number,
    putPart: (partNumber: number, chunk: Blob) => Observable<HttpResponse<any>>,
    onProgress?: (percent: number) => void
  ): Promise<{ PartNumber: number; ETag: string }[]> {
    return uploadMultipartParts(file, partSize, putPart, {
      onProgress: (percent) => {
        onProgress?.(percent);
        globalUploadProgress$.next(percent);
      },
    });
  }

  /**
   * Pipeline completo de upload user-level (start → parts → complete → persist).
   * Notifica progresso (0–100) via callback.
   *
   * `pathPrefix`:
   *  - `undefined` → usa o default por tipo (`Maps`/`Logs`)
   *  - `''` (string vazia) → arquivo salvo na raiz (sem prefixo)
   *  - qualquer outra string → vira o prefixo do `pathFile` (ex.: `'TesteFT/2026'`)
   *
   * Devolve o id (Guid) do arquivo criado pelo persist, ou null se a resposta não o trouxer.
   */
  async uploadUserFile(
    userID: string,
    type: 'map' | 'log',
    file: File,
    onProgress?: (percent: number) => void,
    pathPrefix?: string
  ): Promise<string | null> {
    const PART_SIZE = 10 * 1024 * 1024; // 10 MB

    const startResp = await lastValueFrom(this.StartUserFileUpload(userID, type, file));
    const uploadId = startResp.headers.get('Upload-Id');
    if (!uploadId) {
      throw new Error('Upload-Id não retornado pelo servidor.');
    }
    const objectName = startResp.body?.objectName;
    if (!objectName) {
      throw new Error('objectName não retornado pelo servidor.');
    }
    const fileName: string = startResp.body?.file ?? objectName.split('/').pop() ?? file.name;

    const parts = await this.uploadPartsConcurrently(
      file,
      PART_SIZE,
      (partNumber, chunk) => this.PutFileLogMap(userID, objectName, uploadId, partNumber, chunk),
      onProgress
    );

    await lastValueFrom(
      this.PostFileLogMap(userID, '', type, objectName, uploadId, {
        objectName,
        uploadId,
        parts,
      })
    );

    const resolvedPrefix = pathPrefix === undefined ? (type === 'map' ? 'Maps' : 'Logs') : pathPrefix;
    const pathFile = resolvedPrefix ? `${resolvedPrefix}/${fileName}` : fileName;
    const persistResp = await lastValueFrom(
      this.persistFileUnified(userID, type, { file: fileName, pathFile })
    );
    return persistResp.body?.id ?? null;
  }

  /* ------------------------------------------------------------------------------------------------------------------------------ */

  StartFileUpload(
    userID: string,
    projectID: any,
    type: any,
    file: File,
    taskID: string
  ): Observable<HttpResponse<any>> {
    const url = `${this.fileUrl}/${userID}/upload/start`;
    let contentType;
    switch (type) {
      case "photo":
        contentType = file.type;
        break;
      default:
        contentType = "application/octet-stream";
        break;
    }
    const body = {
      file: file.name,
      contentType: contentType,
      projectID: projectID,
      folder: type,
      taskID: taskID,
      size: file.size / 1024, // Convert size to KB
    };
    return this.http
      .post<any>(url, body, { ...this.httpOptions, observe: "response" })
      .pipe(
        tap((response) => {
          if (response.status === 200) {
            const progress$ = new Subject<number>();
            this.UploadFiles(file, type, userID, projectID, taskID);
          }
        })
      );
  }

  PutFileLogMap(
    userID: string,
    fileName: string,
    uploadId: string,
    partNumber: number,
    file: Blob
  ): Observable<HttpResponse<any>> {
    const url = `${this.fileUrl}/${userID}/upload/${fileName}?uploadId=${uploadId}&partNumber=${partNumber}`;

    const headers = new HttpHeaders({
      "Content-Type": 'application/octet-stream'
    });
      return this.http.put<any>(url, file, {headers:headers, observe:"response"});
    };

  PostFileLogMap(
    userID: string,
    projectID: string,
    type: string,
    fileName: string,
    uploadId: string,
    parts: {
      objectName: string;
      uploadId: string;
      parts: { PartNumber: number; ETag: string }[];
    } = { objectName: fileName, uploadId: uploadId, parts: [] }
  ): Observable<HttpResponse<any>> {
    const url = `${this.fileUrl}/${userID}/upload/complete`;

    return this.http.post<any>(url, parts, {
      ...this.httpOptions,
      observe: "response",
    });
  }

  private isUploading = false;

  public async UploadFiles(
  file: File,
  type: any,
  userID: string,
  projectID: any,
  taskID: string,
): Promise<HttpResponse<any> | Error | void> {
  if (this.isUploading) {
    return;
  }

  this.isUploading = true;
  const PART_SIZE = 10 * 1024 * 1024; // 10MB

  let headResponse: HttpResponse<any>;

  try {
    headResponse = await lastValueFrom(
      this.StartFileUpload(userID, projectID, type, file, taskID)
    );
  } catch (error: any) {
    this.isUploading = false;
    return error;
  }

  if (headResponse.status !== 200) {
    this.isUploading = false;
    return headResponse;
  }

  const uploadId = headResponse.headers.get("Upload-Id");
  if (!uploadId) {
    this.isUploading = false;
    return new Error("Upload-Id não encontrado.");
  }

  const objectName = headResponse.body.objectName;

  try {
    const parts = await this.uploadPartsConcurrently(
      file,
      PART_SIZE,
      (partNumber, chunk) => this.PutFileLogMap(userID, objectName, uploadId, partNumber, chunk)
    );
    const partsUpload: {
      objectName: string;
      uploadId: string;
      parts: { PartNumber: number; ETag: string }[];
    } = { objectName, uploadId, parts };

    const postResponse = await lastValueFrom(
      this.PostFileLogMap(userID, projectID, type, objectName, uploadId, partsUpload)
    );

    return postResponse;
  } catch (error: any) {
    console.error("Erro durante upload multipart:", error);
    return error;
  } finally {
    this.isUploading = false;
  }
}

}
